import { APP_NAME } from "@/lib/brand";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE, downloadFile, blobErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, FileSpreadsheet, Camera } from "lucide-react";

const Presets = ({ preset, setPreset }) => (
  <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 w-fit">
    {[["internal", "Internal report"], ["customer", "Customer report"]].map(([k, l]) => (
      <button key={k} onClick={() => setPreset(k)} className={`px-3 py-1.5 rounded-md text-sm ${preset === k ? "bg-[#2563EB] text-white" : "text-slate-600 hover:bg-white"}`} data-testid={`report-preset-${k}`}>{l}</button>
    ))}
  </div>
);

const SectionList = ({ sections, preset, selected, setSelected }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {sections.map((sec) => {
      const blocked = preset === "customer" && !sec.customer;
      return (
        <label key={sec.key} className={`flex items-center gap-2 rounded-md border p-2 text-sm ${blocked ? "border-slate-100 text-slate-400 bg-slate-50" : "border-slate-200"}`}>
          <Checkbox checked={!blocked && selected.includes(sec.key)} disabled={blocked} onCheckedChange={(c) => setSelected(c ? [...selected, sec.key] : selected.filter((k) => k !== sec.key))} data-testid={`report-section-${sec.key}`} />
          <span className="flex-1">{sec.label}</span>
          {sec.confidential && <Badge variant="outline" className="text-[10px] text-amber-800 bg-amber-50 border-amber-200">Confidential</Badge>}
        </label>
      );
    })}
  </div>
);

const ImagePicker = ({ project, preset, enabled, setEnabled, selected, setSelected }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get(`/projects/${project.project_id}/report/images`, { params: { preset } }).then((r) => {
      setData(r.data);
      const allowed = new Set(r.data.groups.flatMap((g) => g.images.map((i) => i.document_id)));
      setSelected((cur) => (cur === null ? [...allowed] : cur.filter((id) => allowed.has(id))));
    });
  }, [enabled, preset, project.project_id]); // eslint-disable-line
  const all = data ? data.groups.flatMap((g) => g.images.map((i) => i.document_id)) : [];
  const toggle = (ids, on) => setSelected((cur) => (on ? [...new Set([...(cur || []), ...ids])] : (cur || []).filter((id) => !ids.includes(id))));
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="report-images-block">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-800"><Checkbox checked={enabled} onCheckedChange={(c) => setEnabled(!!c)} data-testid="report-include-images" />Include images <span className="text-xs font-normal text-slate-500">— embedded in the matching phase section, each image once</span></label>
      {enabled && data && (
        <>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{(selected || []).length}/{all.length} selected{data.hidden_confidential > 0 && preset === "customer" && <span className="text-amber-700"> · {data.hidden_confidential} confidential image(s) hidden in customer report</span>}</span>
            <span className="flex gap-2"><button className="text-blue-700 hover:underline" onClick={() => toggle(all, true)} data-testid="report-images-select-all">Select all</button><button className="text-blue-700 hover:underline" onClick={() => toggle(all, false)} data-testid="report-images-deselect-all">Deselect all</button></span>
          </div>
          {data.groups.length === 0 && <p className="text-xs text-slate-500">No images available for this report.</p>}
          <div className="max-h-56 overflow-auto space-y-3 pr-1">
            {data.groups.map((g) => (
              <div key={g.phase_id} data-testid={`report-images-group-${g.phase_key}`}>
                <div className="flex items-center justify-between"><p className="overline">{g.phase_name} <span className="text-slate-400">({g.images.length})</span></p><button className="text-[11px] text-blue-700 hover:underline" onClick={() => toggle(g.images.map((i) => i.document_id), !g.images.every((i) => (selected || []).includes(i.document_id)))}>{g.images.every((i) => (selected || []).includes(i.document_id)) ? "Deselect phase" : "Select phase"}</button></div>
                <div className="mt-1 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {g.images.map((im) => {
                    const on = (selected || []).includes(im.document_id);
                    return (
                      <button key={im.document_id} onClick={() => toggle([im.document_id], !on)} className={`relative rounded-md border overflow-hidden text-left ${on ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 opacity-70"}`} title={im.file_name} data-testid={`report-image-${im.document_id}`}>
                        <img src={`${API_BASE}/documents/${im.document_id}/download`} alt={im.caption || im.file_name} className="h-16 w-full object-cover bg-slate-100" loading="lazy" />
                        <div className="px-1.5 py-1 text-[10px] text-slate-700 truncate" title={im.caption ? `${im.caption} (${im.file_name})` : im.file_name}>{im.caption || im.file_name}</div>
                        <span className={`absolute top-1 right-1 h-4 w-4 rounded-sm grid place-items-center text-[10px] ${on ? "bg-[#2563EB] text-white" : "bg-white border border-slate-300"}`}>{on ? "✓" : ""}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const ReportDialog = ({ project, open, onClose }) => {
  const [sections, setSections] = useState([]);
  const [preset, setPreset] = useState("internal");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [includeImages, setIncludeImages] = useState(false);
  const [imageIds, setImageIds] = useState(null);
  useEffect(() => { if (open) { setPreset("internal"); setIncludeImages(false); setImageIds(null); api.get("/reports/sections").then((r) => { setSections(r.data.project); setSelected(r.data.project.map((s) => s.key)); }); } }, [open]);

  const run = async (format) => {
    setBusy(true);
    try {
      const name = await downloadFile(`/projects/${project.project_id}/report`, `${project.project_number}_report.${format}`, { method: "POST", body: { format, preset, sections: selected, include_images: includeImages, image_ids: includeImages ? imageIds || [] : [] } });
      toast.success(`${name} generated`);
      onClose();
    } catch (e) { toast.error(await blobErrorDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="report-dialog">
        <DialogHeader><DialogTitle>Export project report — {project.project_number}</DialogTitle>
          <DialogDescription>Choose a preset and the sections to include. The customer version automatically excludes costing, supplier information, order details, internal issues, confidential images and notes not marked “Share with customer”.</DialogDescription></DialogHeader>
        <Presets preset={preset} setPreset={setPreset} />
        <SectionList sections={sections} preset={preset} selected={selected} setSelected={setSelected} />
        <ImagePicker project={project} preset={preset} enabled={includeImages} setEnabled={setIncludeImages} selected={imageIds} setSelected={setImageIds} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" disabled={busy} onClick={() => run("xlsx")} data-testid="report-export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button>
          <Button disabled={busy} onClick={() => run("pdf")} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="report-export-pdf-btn"><FileText className="h-4 w-4 mr-2" />{busy ? "Generating…" : "PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PrototypeReportButton = ({ project }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [preset, setPreset] = useState("internal");
  const [selected, setSelected] = useState([]);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get(`/projects/${project.project_id}/prototype-report/options`).then((r) => { setOpts(r.data); setSelected(r.data.sections.map((s) => s.key)); setDocs(r.data.documents.filter((d) => d.is_image).map((d) => d.document_id)); });
  }, [open, project.project_id]);
  const run = async () => {
    setBusy(true);
    try {
      const name = await downloadFile(`/projects/${project.project_id}/prototype-report`, `${project.project_number}_prototype.pdf`, { method: "POST", body: { preset, sections: selected, document_ids: docs } });
      toast.success(`${name} generated`); setOpen(false);
    } catch (e) { toast.error(await blobErrorDetail(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="prototype-report-btn"><Camera className="h-4 w-4 mr-2" />Generate prototype report</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="prototype-report-dialog">
          <DialogHeader><DialogTitle>Prototype report — {project.project_number}</DialogTitle><DialogDescription>Built from the Prototyping phase data already in {APP_NAME}: dates, owner, actions, photos, observations, defects and approvals.</DialogDescription></DialogHeader>
          {opts && (
            <>
              <Presets preset={preset} setPreset={setPreset} />
              <SectionList sections={opts.sections} preset={preset} selected={selected} setSelected={setSelected} />
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Photos / files in Prototyping ({opts.documents.length})</p>
                {opts.documents.length === 0 ? <p className="text-xs text-slate-500">No prototype files uploaded yet.</p> : (
                  <div className="flex flex-wrap gap-2">
                    {opts.documents.map((d) => (
                      <label key={d.document_id} className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${d.is_image ? "border-slate-200" : "border-slate-100 text-slate-400"}`}>
                        <Checkbox checked={docs.includes(d.document_id)} disabled={!d.is_image} onCheckedChange={(c) => setDocs(c ? [...docs, d.document_id] : docs.filter((x) => x !== d.document_id))} data-testid={`prototype-photo-${d.document_id}`} />
                        {d.caption || d.file_name}{d.caption && <span className="text-slate-400"> · {d.file_name}</span>}{!d.is_image && " (not an image)"}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button disabled={busy || !opts} onClick={run} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="prototype-report-generate-btn">{busy ? "Generating…" : "Generate PDF"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
